import React, { useState, useEffect } from 'react';
import { 
  Brain, 
  Lightbulb,
  AlertTriangle, 
  Heart,
  Sun,
  FileText,
  RefreshCw,
  Calendar,
  Mail,
  ArrowRight,
  CheckCircle,
  Send,
  X,
  Info,
  Target,
  Users,
  Zap,
  TrendingUp,
  Settings,
  Eye,
  ThumbsUp,
  ThumbsDown,
  Phone,
  Search,
  Book
} from 'lucide-react';

// Import components from main site
import HeaderNav from './HeaderNav';
import FooterLan from './FooterLan';

// Color palette aligned with main site
const COLORS = {
  primary: '#ce2223',
  primaryDark: '#b01a1b',
  primaryLight: '#f8e7e7',
  primaryLighter: '#fef2f2',
  secondary: '#fdb52a',
  secondaryDark: '#e09c18',
  secondaryLight: '#fff8eb',
  black: '#111827',
  darkGray: '#374151',
  mediumGray: '#6b7280',
  lightGray: '#e5e7eb',
  offWhite: '#f9fafb',
  white: '#ffffff',
  success: '#10b981',
  successLight: '#ecfdf5'
};

// Six Thinking Hats framework with consistent styling
const THINKING_HATS = {
  white: {
    name: 'White Hat',
    focus: 'Facts & Information',
    description: 'Objective data, facts, and information',
    bgClass: 'bg-gray-50',
    borderClass: 'border-gray-300',
    textClass: 'text-gray-800',
    iconClass: 'text-gray-600',
    iconBg: 'bg-gray-100',
    icon: FileText,
    prompt: 'What are the key facts, data, and objective information we need to consider?'
  },
  red: {
    name: 'Red Hat',
    focus: 'Emotions & Feelings',
    description: 'Emotions, feelings, and intuitive responses',
    bgClass: 'bg-red-50',
    borderClass: 'border-red-300',
    textClass: 'text-red-800',
    iconClass: 'text-red-600',
    iconBg: 'bg-red-100',
    icon: Heart,
    prompt: 'What are the emotional reactions, gut feelings, and human concerns?'
  },
  black: {
    name: 'Black Hat',
    focus: 'Critical Thinking',
    description: 'Caution, critical analysis, and potential problems',
    bgClass: 'bg-gray-800',
    borderClass: 'border-gray-700',
    textClass: 'text-white',
    iconClass: 'text-gray-300',
    iconBg: 'bg-gray-700',
    icon: AlertTriangle,
    prompt: 'What are the risks, challenges, and potential problems we should be aware of?'
  },
  yellow: {
    name: 'Yellow Hat',
    focus: 'Positive Thinking',
    description: 'Optimism, benefits, and positive outcomes',
    bgClass: 'bg-yellow-50',
    borderClass: 'border-yellow-300',
    textClass: 'text-yellow-800',
    iconClass: 'text-yellow-600',
    iconBg: 'bg-yellow-100',
    icon: Sun,
    prompt: 'What are the benefits, opportunities, and positive outcomes we can achieve?'
  },
  green: {
    name: 'Green Hat',
    focus: 'Creative Thinking',
    description: 'Creativity, alternatives, and new ideas',
    bgClass: 'bg-green-50',
    borderClass: 'border-green-300',
    textClass: 'text-green-800',
    iconClass: 'text-green-600',
    iconBg: 'bg-green-100',
    icon: Lightbulb,
    prompt: 'What creative solutions, alternatives, and innovative ideas can we explore?'
  },
  blue: {
    name: 'Blue Hat',
    focus: 'Process Control',
    description: 'Process, control, and next steps',
    bgClass: 'bg-blue-50',
    borderClass: 'border-blue-300',
    textClass: 'text-blue-800',
    iconClass: 'text-blue-600',
    iconBg: 'bg-blue-100',
    icon: Settings,
    prompt: 'What process should we follow and what are the next steps to move forward?'
  }
};

// Example innovation challenges
const EXAMPLE_CHALLENGES = [
  {
    text: "How can we improve employee engagement and reduce turnover in our organization?",
    icon: <Users className="h-4 w-4" />,
    color: "bg-blue-50 text-blue-700 border-blue-200"
  },
  {
    text: "We need to innovate our product line to attract younger customers",
    icon: <Target className="h-4 w-4" />,
    color: "bg-green-50 text-green-700 border-green-200"
  },
  {
    text: "How do we transform our traditional business model for the digital era?",
    icon: <Zap className="h-4 w-4" />,
    color: "bg-purple-50 text-purple-700 border-purple-200"
  },
  {
    text: "Our team struggles with creative problem-solving during brainstorming meetings",
    icon: <Brain className="h-4 w-4" />,
    color: "bg-orange-50 text-orange-700 border-orange-200"
  },
  {
    text: "We want to develop more sustainable and environmentally friendly business practices",
    icon: <TrendingUp className="h-4 w-4" />,
    color: "bg-emerald-50 text-emerald-700 border-emerald-200"
  },
  {
    text: "How can we better compete with new startups disrupting our industry?",
    icon: <Eye className="h-4 w-4" />,
    color: "bg-red-50 text-red-700 border-red-200"
  }
];

// Enhanced Button component
const Button = ({ 
  children, 
  onClick, 
  isLoading = false, 
  fullWidth = false, 
  variant = 'primary', 
  icon, 
  className = '', 
  disabled = false,
  size = 'md'
}) => {
  const baseStyle = "font-medium transition-all duration-300 flex items-center justify-center shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500";
  
  const sizes = {
    sm: "py-2 px-4 text-sm rounded-md",
    md: "py-3 px-6 text-base rounded-lg",
    lg: "py-4 px-8 text-lg rounded-xl"
  };
  
  const variants = {
    primary: `bg-red-600 hover:bg-red-700 text-white ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'}`,
    outline: `bg-white border-2 border-red-600 text-red-600 hover:bg-red-50 ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'}`,
    secondary: `bg-gray-100 hover:bg-gray-200 text-gray-700 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`
  };

  return (
    <button
      onClick={onClick}
      disabled={isLoading || disabled}
      className={`${baseStyle} ${sizes[size]} ${variants[variant]} ${fullWidth ? 'w-full' : ''} ${className}`}
    >
      {isLoading ? (
        <RefreshCw className="h-5 w-5 animate-spin mr-2" />
      ) : (
        icon && <span className="mr-2">{icon}</span>
      )}
      {children}
    </button>
  );
};

// Toast notification with better mobile support
const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => onClose(), 5000);
    return () => clearTimeout(timer);
  }, [onClose]);
  
  const styles = {
    success: 'bg-green-50 text-green-800 border-green-200',
    error: 'bg-red-50 text-red-800 border-red-200',
    info: 'bg-blue-50 text-blue-800 border-blue-200'
  };
  
  return (
    <div className={`fixed top-20 left-4 right-4 sm:right-4 sm:left-auto sm:max-w-sm z-50 px-4 py-3 rounded-lg shadow-lg border flex items-center gap-3 ${styles[type]}`}>
      <CheckCircle className="h-5 w-5 flex-shrink-0" />
      <p className="font-medium flex-1">{message}</p>
      <button 
        onClick={onClose} 
        className="flex-shrink-0 p-1 hover:bg-black/10 rounded"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

// Thinking Hat Card with mobile improvements
const HatCard = ({ hatKey, insights, index }) => {
  const hat = THINKING_HATS[hatKey];
  const HatIcon = hat.icon;
  
  return (
    <article className={`rounded-xl border-2 ${hat.borderClass} ${hat.bgClass} shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden`}>
      <div className="p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 ${hat.borderClass} ${hat.iconBg} flex items-center justify-center flex-shrink-0`}>
            <HatIcon className={`h-5 w-5 sm:h-6 sm:w-6 ${hat.iconClass}`} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className={`font-bold text-base sm:text-lg ${hat.textClass} leading-tight`}>{hat.name}</h3>
            <p className={`text-xs sm:text-sm ${hat.textClass} opacity-80`}>{hat.focus}</p>
          </div>
        </div>
        
        {/* Description */}
        <p className={`text-xs sm:text-sm ${hat.textClass} opacity-70 mb-4 leading-relaxed`}>{hat.description}</p>
        
        {/* Insights */}
        <div className="space-y-2 sm:space-y-3">
          {insights.map((insight, idx) => (
            <div key={idx} className={`${hatKey === 'black' ? 'bg-gray-700' : 'bg-white'} rounded-lg p-3 border ${hat.borderClass}`}>
              <div className="flex items-start gap-3">
                <ArrowRight className={`h-3 w-3 sm:h-4 sm:w-4 ${hat.iconClass} mt-1 flex-shrink-0`} />
                <p className={`text-xs sm:text-sm ${hat.textClass} leading-relaxed`}>{insight}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </article>
  );
};

// Loading animation with better mobile support
const LoadingAnimation = () => {
  const [currentHat, setCurrentHat] = useState(0);
  const hatKeys = Object.keys(THINKING_HATS);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHat(prev => (prev + 1) % hatKeys.length);
    }, 800);
    return () => clearInterval(interval);
  }, [hatKeys.length]);
  
  const currentHatData = THINKING_HATS[hatKeys[currentHat]];
  const CurrentIcon = currentHatData.icon;
  
  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 sm:p-8 max-w-md mx-auto text-center">
      <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-6 relative">
        <div className={`w-full h-full rounded-full ${currentHatData.bgClass} border-2 ${currentHatData.borderClass} flex items-center justify-center transition-all duration-500`}>
          <CurrentIcon className={`h-6 w-6 sm:h-8 sm:w-8 ${currentHatData.iconClass}`} />
        </div>
        <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 rounded-full animate-pulse"></div>
      </div>
      
      <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Thinking with Six Hats</h3>
      <div className="flex items-center justify-center gap-2 text-red-600 mb-4">
        <Search className="h-5 w-5" />
        <span className="font-medium text-sm sm:text-base">Analyzing from {currentHatData.name}...</span>
      </div>
      <p className="text-gray-600 text-sm leading-relaxed">
        This takes about 30 seconds while we examine your challenge from all six thinking perspectives to provide comprehensive insights.
      </p>
    </div>
  );
};

// Email capture component with mobile improvements
const EmailCapture = ({ onSubmit, isSubmitting }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  
  const handleSubmit = () => {
    if (!email || !email.includes('@')) return;
    onSubmit({ email, name });
  };
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
      <div className="text-center mb-6">
        <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-3">
          <Book className="h-6 w-6 text-red-600" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">Get Complete Innovation Report</h3>
        <p className="text-gray-600 text-sm">Download detailed analysis with action plan, facilitation guide, and team workshop templates</p>
      </div>
      
      <div className="mb-6 space-y-3">
        <h4 className="font-medium text-gray-900 text-sm">Your report includes:</h4>
        {[
          "Complete six thinking hats analysis with detailed insights for each perspective",
          "Facilitation guide for running team workshops using this methodology", 
          "Action plan with specific next steps and implementation recommendations",
          "Team workshop templates and discussion prompts for effective sessions"
        ].map((item, idx) => (
          <div key={idx} className="flex items-start gap-3 text-sm text-gray-600">
            <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
            <span className="leading-relaxed">{item}</span>
          </div>
        ))}
      </div>
      
      <div className="space-y-4">
        <div>
          <label htmlFor="name-input" className="block text-sm font-medium text-gray-700 mb-2">
            Name
          </label>
          <input
            id="name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            className="w-full p-3 border border-gray-300 rounded-lg focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none"
          />
        </div>
        
        <div>
          <label htmlFor="email-input" className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <input
            id="email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            required
            className="w-full p-3 border border-gray-300 rounded-lg focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none"
          />
        </div>
        
        <Button
          onClick={handleSubmit}
          disabled={!email || !email.includes('@')}
          isLoading={isSubmitting}
          icon={<Send className="h-4 w-4" />}
          fullWidth
        >
          Send Innovation Report
        </Button>
      </div>
      
      <p className="text-xs text-gray-500 text-center mt-4">
        We respect your privacy and never share your information
      </p>
    </div>
  );
};

// Success message
const SubmissionSuccess = ({ email }) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 sm:p-8 text-center max-w-md mx-auto">
      <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
        <CheckCircle className="h-8 w-8 text-green-600" />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-3">Innovation Report Sent!</h3>
      <p className="text-gray-600 mb-4">
        Check your email at <span className="font-semibold text-gray-900">{email}</span> for your comprehensive six thinking hats analysis with facilitation guide.
      </p>
      <p className="text-gray-600 text-sm">
        Our innovation experts will follow up to help you facilitate effective team sessions using these insights.
      </p>
    </div>
  );
};

// Example challenge card with better mobile design
const ExampleCard = ({ challenge, onClick }) => {
  return (
    <button
      onClick={() => onClick(challenge.text)}
      className="p-4 bg-white border border-gray-200 rounded-lg text-left hover:border-red-300 hover:shadow-sm transition-all w-full group focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-8 h-8 rounded-lg border ${challenge.color} flex items-center justify-center`}>
          {challenge.icon}
        </div>
        <div className="text-xs text-gray-500 group-hover:text-red-600 transition-colors font-medium">
          Try this example →
        </div>
      </div>
      <p className="text-gray-800 font-medium text-sm leading-relaxed">
        {challenge.text}
      </p>
    </button>
  );
};

// Main component
const SixThinkingHatsTool = () => {
  const [challenge, setChallenge] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hatInsights, setHatInsights] = useState({});
  const [toast, setToast] = useState(null);
  const [isEmailSubmitting, setIsEmailSubmitting] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  
  const generateInsights = async () => {
    if (!challenge.trim()) {
      setToast({ message: 'Please describe your innovation challenge first', type: 'error' });
      return;
    }
    
    setIsLoading(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    try {
      const response = await fetch('https://proxyapi-te6hpgqhgq-uc.a.run.app', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-3-5-sonnet-20241022',
          max_tokens: 4000,
          temperature: 0.7,
          messages: [{
            role: 'user',
            content: `Using Edward de Bono's Six Thinking Hats method, analyze this innovation challenge: "${challenge}"

For each thinking hat, provide 3 specific, actionable insights:

WHITE HAT (Facts & Information): What objective data, facts, and information should be considered?
RED HAT (Emotions & Feelings): What emotional reactions, feelings, and human concerns are involved?
BLACK HAT (Critical Thinking): What risks, challenges, and potential problems should be addressed?
YELLOW HAT (Positive Thinking): What benefits, opportunities, and positive outcomes are possible?
GREEN HAT (Creative Thinking): What creative solutions, alternatives, and innovative approaches can be explored?
BLUE HAT (Process Control): What process steps and next actions should be taken?

Make each insight specific, practical, and actionable. Keep language simple and business-focused.

Respond in this JSON format:
{
  "insights": {
    "white": ["insight 1", "insight 2", "insight 3"],
    "red": ["insight 1", "insight 2", "insight 3"],
    "black": ["insight 1", "insight 2", "insight 3"],
    "yellow": ["insight 1", "insight 2", "insight 3"],
    "green": ["insight 1", "insight 2", "insight 3"],
    "blue": ["insight 1", "insight 2", "insight 3"]
  }
}`
          }]
        })
      });

      if (!response.ok) throw new Error('Failed to generate insights');
      
      const data = await response.json();
      const jsonMatch = data.content[0].text.match(/\{[\s\S]*\}/);
      
      if (jsonMatch) {
        const insightsData = JSON.parse(jsonMatch[0]);
        setTimeout(() => {
          setHatInsights(insightsData.insights || {});
          setIsLoading(false);
        }, 2000);
      } else {
        throw new Error('Could not parse response');
      }
    } catch (error) {
      console.error('Error:', error);
      setIsLoading(false);
      setToast({ message: 'Unable to generate insights. Please try again.', type: 'error' });
    }
  };
  
  const handleEmailSubmit = (data) => {
    setIsEmailSubmitting(true);
    setTimeout(() => {
      setIsEmailSubmitting(false);
      setEmailSubmitted(true);
      setSubmittedEmail(data.email);
      setToast({ message: 'Innovation report sent to your email successfully!', type: 'success' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 1500);
  };
  
  const handleReset = () => {
    setChallenge('');
    setHatInsights({});
    setEmailSubmitted(false);
    setSubmittedEmail('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const handleSelectExample = (text) => {
    setChallenge(text);
    const textarea = document.getElementById('challenge-input');
    if (textarea) {
      setTimeout(() => {
        textarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
        textarea.focus();
      }, 100);
    }
  };
  
  const hasInsights = Object.keys(hatInsights).length > 0;

  // SEO metadata (would be handled by helmet in real implementation)
  useEffect(() => {
    document.title = "Six Thinking Hats Innovation Tool - Creative Problem Solving Method | Lusidea";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Use Edward de Bono\'s Six Thinking Hats method to analyze innovation challenges from multiple perspectives. Get comprehensive insights for creative problem-solving and breakthrough thinking.');
    }
  }, []);
  
  return (
    <div className="min-h-screen bg-gray-50">
      <HeaderNav />
      
      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      
      {/* Header Section */}
      <header className="bg-white pt-24 sm:pt-28 pb-12 sm:pb-16 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="w-16 h-16 mx-auto bg-red-100 rounded-2xl flex items-center justify-center mb-6">
            <Brain className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            Six Thinking Hats Innovation Tool
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Unlock creative solutions by examining your challenge from six different thinking perspectives using Edward de Bono's proven method
          </p>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        
        {/* Input Section */}
        {!isLoading && !hasInsights && !emailSubmitted && (
          <div className="space-y-8">
            
            {/* Main Input */}
            <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                What innovation challenge are you facing?
              </h2>
              <div className="mb-6">
                <label htmlFor="challenge-input" className="block text-sm font-medium text-gray-700 mb-3">
                  Describe your challenge or problem
                </label>
                <textarea
                  id="challenge-input"
                  value={challenge}
                  onChange={(e) => setChallenge(e.target.value)}
                  placeholder="Describe your innovation challenge... For example: 'Our team struggles to come up with creative solutions during brainstorming sessions' or 'We need to innovate our customer service approach but are stuck in traditional thinking' or 'How can we improve employee engagement while maintaining productivity?'"
                  className="w-full p-4 border border-gray-300 rounded-lg focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none min-h-32 text-gray-800 resize-y"
                  rows={6}
                />
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mt-3 gap-2">
                  <span className="text-sm text-gray-500">The more specific you are, the better insights you'll receive</span>
                  <span className="text-sm text-gray-400">{challenge.length} characters</span>
                </div>
              </div>
              <div className="text-center">
                <Button
                  onClick={generateInsights}
                  disabled={!challenge.trim()}
                  icon={<Search className="h-5 w-5" />}
                  size="lg"
                >
                  Start Six Thinking Hats Analysis
                </Button>
              </div>
            </section>
            
            {/* Example Challenges */}
            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-6 text-center">
                Not sure where to start? Try one of these innovation challenges:
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {EXAMPLE_CHALLENGES.map((challenge, idx) => (
                  <ExampleCard
                    key={idx}
                    challenge={challenge}
                    onClick={handleSelectExample}
                  />
                ))}
              </div>
            </section>
            
            {/* How it works */}
            <section className="bg-blue-50 rounded-xl p-6 border border-blue-200">
              <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                <Info className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-blue-900 mb-3">How Six Thinking Hats works</h3>
                  <p className="text-blue-800 text-sm leading-relaxed">
                    This proven innovation method by Edward de Bono examines your challenge from six different thinking perspectives. 
                    Each "hat" represents a different mode of thinking - facts, emotions, caution, optimism, creativity, and process - 
                    ensuring comprehensive analysis and breakthrough solutions.
                  </p>
                </div>
              </div>
            </section>
          </div>
        )}
        
        {/* Loading State */}
        {isLoading && <LoadingAnimation />}
        
        {/* Results */}
        {!isLoading && hasInsights && !emailSubmitted && (
          <div className="space-y-8">
            {/* Results Header */}
            <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">Your Six Thinking Hats Analysis</h2>
                  <p className="text-gray-600 text-sm">
                    Challenge: <span className="font-medium">"{challenge.slice(0, 100)}..."</span>
                  </p>
                </div>
              </div>
            </section>

            {/* Hat Legend Overview */}
            <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h3 className="font-bold text-gray-900 mb-4 text-center">The Six Thinking Perspectives</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {Object.entries(THINKING_HATS).map(([key, hat]) => {
                  const HatIcon = hat.icon;
                  return (
                    <div key={key} className="flex flex-col items-center text-center p-3 rounded-lg bg-gray-50">
                      <div className={`w-10 h-10 rounded-full ${hat.iconBg} border ${hat.borderClass} flex items-center justify-center mb-2`}>
                        <HatIcon className={`h-4 w-4 ${hat.iconClass}`} />
                      </div>
                      <span className="text-xs text-gray-700 font-medium">{hat.name}</span>
                      <span className="text-xs text-gray-500 mt-1">{hat.focus}</span>
                    </div>
                  );
                })}
              </div>
            </section>
            
            {/* Hat Cards */}
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {Object.entries(THINKING_HATS).map(([hatKey, _]) => (
                <HatCard
                  key={hatKey}
                  hatKey={hatKey}
                  insights={hatInsights[hatKey] || []}
                  index={0}
                />
              ))}
            </section>

            {/* Email Capture */}
            <section className="max-w-2xl mx-auto">
              <EmailCapture onSubmit={handleEmailSubmit} isSubmitting={isEmailSubmitting} />
            </section>

            {/* Facilitation Help CTA */}
            <section className="bg-gradient-to-r from-red-50 to-red-100 rounded-xl border border-red-200 p-6 sm:p-8 text-center">
              <div className="max-w-3xl mx-auto">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Phone className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Need Workshop Facilitation?</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Get expert guidance on running Six Thinking Hats workshops with your team. 
                  Our innovation experts will help you facilitate effective sessions and turn insights into action.
                </p>
                <a
                  href="https://calendly.com/lusidea/innovation-consultation"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 py-3 px-6 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 shadow-sm"
                >
                  <Calendar className="h-5 w-5" />
                  Book Innovation Session
                </a>
              </div>
            </section>

            {/* Reset Action */}
            <div className="text-center pt-4">
              <Button
                onClick={handleReset}
                variant="outline"
                icon={<RefreshCw className="h-4 w-4" />}
              >
                Analyze Another Challenge
              </Button>
            </div>
          </div>
        )}
        
        {/* Success State */}
        {emailSubmitted && <SubmissionSuccess email={submittedEmail} />}
      </main>
      
      <FooterLan />
      
      <style jsx global>{`
        /* Enhanced focus states for accessibility */
        input:focus, textarea:focus {
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
        }
        
        /* Smooth transitions */
        * {
          transition-property: background-color, border-color, color, fill, stroke, opacity, box-shadow, transform;
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
          transition-duration: 150ms;
        }
      `}</style>
    </div>
  );
};

export default SixThinkingHatsTool;